import { useMutation, useQueryClient } from '@tanstack/react-query';
import PaymentsAPIServiceV2 from './PaymentsServiceV2';
import { PAYMENT_QUERY_KEYS } from './paymentsQuery';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useTranslation } from 'react-i18next';

const paymentsService = PaymentsAPIServiceV2.getInstance();

export const usePaymentsMutations = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useNotificationsSnackbar();
    const { t } = useTranslation();

    const updateCommonDataFlag = useMutation({
        mutationFn: paymentsService.updateCompanyDetails,
        onSuccess: () => {
            //TODO: get company to query
            setAlert(t('common_data_updated'), 'success');
        },
        onError: (error) => {
            setAlert(error.message, 'error');
        }
    });

    const updateCompanyDetails = useMutation({
        mutationFn: paymentsService.updateCompanyDetails,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getImportFilters]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getCurrentCompanyDetails]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const setupPlanday = useMutation({
        mutationFn: paymentsService.plandaySetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const setupTamigo = useMutation({
        mutationFn: paymentsService.tamigoSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.TAMIGO_COMPANY_INFO]
            });
        }
    });

    const setupWorkfeed = useMutation({
        mutationFn: paymentsService.workfeedSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const setupTitanPlan = useMutation({
        mutationFn: paymentsService.titanPlanSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const refreshPlandayShiftsCache = useMutation({
        mutationFn: paymentsService.refreshPlandayShiftsCache,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getCurrentCompanyDetails]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const updatePlandayChildCompany = useMutation({
        mutationFn: paymentsService.updatePlandayChildCompany,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const setupSameSystem = useMutation({
        mutationFn: paymentsService.sameSystemSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                // queryKey: [PAYMENT_QUERY_KEYS.SAME_SYSTEM_COMPANY_INFO]
            });
        }
    });

    const setupDataLon = useMutation({
        mutationFn: paymentsService.dataLonSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const setupDatevLodas = useMutation({
        mutationFn: paymentsService.datevLodasSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const setupTrivec = useMutation({
        mutationFn: paymentsService.trivecSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const connectWorkForce = useMutation({
        mutationFn: paymentsService.workForceConnect,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const syncSyncBankTransactionRecords = useMutation({
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllBankTransactionReports]
            });
        },
        // A thrown request (500/timeout/network drop) never reaches the
        // success effect that decodes the partial/total-failure summary, so
        // surface the failure here — otherwise a broken sync looks like nothing
        // happened.
        onError: () => {
            setAlert(t('transaction_monitoring.sync_failed'), 'error');
        },
        mutationFn: paymentsService.getSyncBankTransactionRecords
    });

    // Connect / re-authorize a house account's Aiia bank feed. The caller
    // redirects the browser to the returned connectUrl; nothing here reads or
    // persists the state nonce.
    const connectHouseAccountAiia = useMutation({
        mutationFn: paymentsService.connectHouseAccountAiia
    });

    const exchangeHouseAccountAiia = useMutation({
        mutationFn: paymentsService.exchangeHouseAccountAiia,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getHouseAccountConnectionStatus]
            });
        }
    });

    const selectHouseAccountAiiaAccount = useMutation({
        mutationFn: paymentsService.selectHouseAccountAiiaAccount,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getHouseAccountConnectionStatus]
            });
        }
    });

    // Invalidate by the shared key prefix so both keyed variants refetch — the
    // management list and the Transaction Monitoring selector — since a newly
    // created account must be selectable immediately.
    const createHouseAccount = useMutation({
        mutationFn: paymentsService.createHouseAccount,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getSelectableHouseAccounts]
            });
        }
    });

    const rebindCompanyHouseAccount = useMutation({
        mutationFn: paymentsService.rebindCompanyHouseAccount,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getCompanyHouseAccountBinding]
            });
        }
    });

    const updateHouseAccount = useMutation({
        mutationFn: paymentsService.updateHouseAccount,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getSelectableHouseAccounts]
            });
            // Evict rather than just invalidate: the dialog is closed at this point
            // (query inactive), so a mere invalidate would only mark it stale and
            // serve the old cached values on the very next open while it quietly
            // refetches in the background — evicting forces a real fetch instead.
            queryClient.removeQueries({
                queryKey: [
                    PAYMENT_QUERY_KEYS.getHouseAccountBankMaterial,
                    variables.instaPaidHouseAccountId
                ]
            });
        }
    });

    const deleteHouseAccount = useMutation({
        mutationFn: paymentsService.deleteHouseAccount,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getSelectableHouseAccounts]
            });
        }
    });

    const ignoreBankTransactionRecord = useMutation({
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllBankTransactionReports]
            });
        },
        mutationFn: paymentsService.ignoreBankTransactionRecord
    });

    const topUpCompanyAccountWithTransaction = useMutation({
        mutationFn: paymentsService.topUpCompanyAccountWithTransaction,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllBankTransactionReports]
            });
        }
    });

    const cancelTransaction = useMutation({
        mutationFn: paymentsService.cancelTransaction,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllBankTransactionReports]
            });
        }
    });

    const importUsers = useMutation({
        mutationFn: paymentsService.importUsers,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getLast10Jobs]
            });
        }
    });

    const cancelJob = useMutation({
        mutationFn: paymentsService.cancelJob,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getLast10Jobs]
            });
        }
    });

    const setImportFilters = useMutation({
        mutationFn: paymentsService.setImportFilter,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getImportFilters]
            });
        }
    });

    const setImportFiltersForSource = useMutation({
        mutationFn: ({ sourceType, data }: { sourceType: string; data: any }) =>
            paymentsService.setImportFilterForSource(sourceType, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getImportFilters]
            });
        }
    });

    const proloenSetup = useMutation({
        mutationFn: paymentsService.proloenSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                //queryKey: [PAYMENT_QUERY_KEYS.PROLOEN_COMPANY_S]
            });
        }
    });

    const eposSetup = useMutation({
        mutationFn: paymentsService.eposSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const staffSetup = useMutation({
        mutationFn: paymentsService.staffSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const lessorSetup = useMutation({
        mutationFn: paymentsService.lessorSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const quinyxSetup = useMutation({
        mutationFn: paymentsService.quinyxSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const dagrofaLonSetup = useMutation({
        mutationFn: paymentsService.dagrofaLonSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const d365FinanceOperationsSetup = useMutation({
        mutationFn: paymentsService.d365FinanceOperationsSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const getAiiaAuthorizeUrl = useMutation({
        mutationFn: paymentsService.getAiiaAuthorizeUrl
    });

    const rejectTransactions = useMutation({
        mutationFn: paymentsService.rejectTransactions,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPendingTransactions]
            });
        }
    });

    const approveTransactions = useMutation({
        mutationFn: paymentsService.approveTransactions,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPendingTransactions]
            });
        }
    });

    const succeedTransactionInSalarySystem = useMutation({
        mutationFn: paymentsService.succeedTransactionInSalarySystem,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPendingTransactions]
            });
        }
    });

    const failTransactionInSalarySystem = useMutation({
        mutationFn: paymentsService.failTransactionInSalarySystem,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPendingTransactions]
            });
        }
    });

    const addPOSLocation = useMutation({
        mutationFn: paymentsService.addPOSLocation,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });
    const addTrivecLocation = useMutation({
        mutationFn: paymentsService.addTrivecLocation,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocations]
            });
        }
    });
    const setPOSLocationDepartments = useMutation({
        mutationFn: paymentsService.setPOSLocationDepartments,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });

    const deletePOSLocation = useMutation({
        mutationFn: paymentsService.deletePOSLocation,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });

    const addManualPosLocation = useMutation({
        mutationFn: paymentsService.addManualPosLocation,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments]
            });
        }
    });

    const addManualPosJournal = useMutation({
        mutationFn: paymentsService.addManualPosJournal,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getManualPosJournals]
            });
            // A new journal becomes a daily settlement once tips are calculated,
            // so refresh the InstaTips history list too.
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getTipsHistory]
            });
        }
    });

    const updateManualPosJournal = useMutation({
        mutationFn: paymentsService.updateManualPosJournal,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getManualPosJournals]
            });
            // Editing an amount can redistribute tips, so tips history is stale too.
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getTipsHistory]
            });
        }
    });

    const deleteManualPosJournal = useMutation({
        mutationFn: paymentsService.deleteManualPosJournal,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getManualPosJournals]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getTipsHistory]
            });
        }
    });

    const setTipsExclusion = useMutation({
        mutationFn: paymentsService.setTipsExclusion,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getTipsExclusions]
            });
            variables.items.forEach((item) => {
                queryClient.invalidateQueries({
                    queryKey: [PAYMENT_QUERY_KEYS.getUserTips, item.userId]
                });
            });
        },
        onError: (error) => {
            setAlert(error.message, 'error');
        }
    });

    const setDepartmentSalaryType = useMutation({
        mutationFn: paymentsService.setDepartmentSalaryType,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getDepartmentSalaryTypes]
            });
        },
        onError: (error) => {
            setAlert(error.message, 'error');
        }
    });

    const deleteDepartmentSalaryType = useMutation({
        mutationFn: paymentsService.deleteDepartmentSalaryType,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getDepartmentSalaryTypes]
            });
        },
        onError: (error) => {
            setAlert(error.message, 'error');
        }
    });

    const putEmployeeGroup = useMutation({
        mutationFn: paymentsService.putTipsEmployeeGroup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSEmployeeGroup]
            });
        }
    });

    const putPOSJournalLines = useMutation({
        mutationFn: paymentsService.putPOSJournalLines,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });

    const syncPOSJournalLineFilters = useMutation({
        mutationFn: paymentsService.syncPOSJournalLinesFilters,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });

    const syncTrivecJournals = useMutation({
        mutationFn: paymentsService.syncTrivecJournals,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });

    const putTrivecGroupFilters = useMutation({
        mutationFn: paymentsService.putTrivecGroupFilters,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getTrivecGroupFilters]
            });
        }
    });

    const balanceTextSummaryReset = useMutation({
        mutationFn: paymentsService.balanceTextSummaryReset,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getCurrentCompanyDetails]
            });
        }
    });

    const createEmploymentGroupTemplate = useMutation({
        mutationFn: paymentsService.createEmploymentGroupTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmploymentGroupTemplates]
            });
        }
    });

    const deleteEmploymentGroupTemplate = useMutation({
        mutationFn: paymentsService.deleteEmploymentGroupTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmploymentGroupTemplates]
            });
        }
    });

    const setEmployeeGroupTemplate = useMutation({
        mutationFn: paymentsService.setEmployeeGroupTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments]
            });
        }
    });

    const updateEmployeeGroupTemplate = useMutation({
        mutationFn: paymentsService.updateEmployeeGroupTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmploymentGroupTemplates]
            });
        }
    });

    const updateJournalAmount = useMutation({
        mutationFn: paymentsService.updateJournalAmount,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getTipsHistory]
            });
        }
    });
    const redistributeTipsForDate = useMutation({
        mutationFn: paymentsService.redistributeTipsForDate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getTipsHistory]
            });
        }
    });
    const documentUpload = useMutation({
        mutationFn: paymentsService.documentUpload,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmployeeDocuments]
            });
        }
    });

    const deleteDocument = useMutation({
        mutationFn: paymentsService.deleteDocument,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmployeeDocuments]
            });
        }
    });

    const createDocument = useMutation({
        mutationFn: paymentsService.createDocument,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmployeeDocuments]
            });
        }
    });

    const updateDocument = useMutation({
        mutationFn: ({ documentId, data }: { documentId: string; data: any }) =>
            paymentsService.updateDocument(documentId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmployeeDocuments]
            });
        }
    });

    const updateEmployeeAutoApprovalTypes = useMutation({
        mutationFn: paymentsService.postEmployeeTypesUpdate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [
                    PAYMENT_QUERY_KEYS.EMPLOYEE_AUTO_APPROVAL_TYPE_SETTINGS
                ]
            });
            setAlert('Updated!', 'success');
        },
        onError: () => {
            setAlert('Failed to update!', 'error');
        }
    });

    const sendToggleTipsInBalanceVerificationEmail = useMutation({
        mutationFn: (companyId: string) =>
            paymentsService.sendToggleTipsInBalanceVerificationEmail(companyId)
    });

    const toggleTipsInBalance = useMutation({
        mutationFn: (request: {
            companyId: string;
            tipsLive: boolean;
            verificationCode: string;
        }) => paymentsService.toggleTipsInBalance(request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getCurrentCompanyDetails]
            });
        }
    });

    const createCompanyAccountTransaction = useMutation({
        mutationFn: paymentsService.createCompanyAccountTransaction,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getCompanyAccounts]
            });
        }
    });

    return {
        createCompanyAccountTransaction,
        setupPlanday,
        updatePlandayChildCompany,
        refreshPlandayShiftsCache,
        updateCommonDataFlag,
        updateCompanyDetails,
        setupTamigo,
        setupWorkfeed,
        setupDataLon,
        setupDatevLodas,
        setupTrivec,
        connectWorkForce,
        setupSameSystem,
        setupTitanPlan,
        syncSyncBankTransactionRecords,
        connectHouseAccountAiia,
        exchangeHouseAccountAiia,
        selectHouseAccountAiiaAccount,
        createHouseAccount,
        updateHouseAccount,
        deleteHouseAccount,
        rebindCompanyHouseAccount,
        ignoreBankTransactionRecord,
        topUpCompanyAccountWithTransaction,
        cancelTransaction,
        importUsers,
        cancelJob,
        proloenSetup,
        setImportFilters,
        setImportFiltersForSource,
        getAiiaAuthorizeUrl,
        rejectTransactions,
        approveTransactions,
        succeedTransactionInSalarySystem,
        failTransactionInSalarySystem,
        addPOSLocation,
        addTrivecLocation,
        setPOSLocationDepartments,
        deletePOSLocation,
        putEmployeeGroup,
        putPOSJournalLines,
        eposSetup,
        staffSetup,
        lessorSetup,
        quinyxSetup,
        dagrofaLonSetup,
        d365FinanceOperationsSetup,
        balanceTextSummaryReset,
        createEmploymentGroupTemplate,
        deleteEmploymentGroupTemplate,
        setEmployeeGroupTemplate,
        updateEmployeeGroupTemplate,
        syncPOSJournalLineFilters,
        syncTrivecJournals,
        putTrivecGroupFilters,
        updateJournalAmount,
        redistributeTipsForDate,
        documentUpload,
        deleteDocument,
        createDocument,
        updateDocument,
        updateEmployeeAutoApprovalTypes,
        sendToggleTipsInBalanceVerificationEmail,
        toggleTipsInBalance,
        setTipsExclusion,
        setDepartmentSalaryType,
        deleteDepartmentSalaryType,
        addManualPosLocation,
        addManualPosJournal,
        updateManualPosJournal,
        deleteManualPosJournal
    };
};
